import React, { useState } from 'react';
import { 
    Layout, 
    Card, 
    Row, 
    Col, 
    Table, 
    Tag, 
    Button, 
    Space, 
    Select, 
    message, 
    Typography 
} from 'antd';
import { 
    SearchOutlined, 
    ClearOutlined, 
    FileExcelOutlined, 
    ArrowLeftOutlined,
    DollarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import axiosClient from '../../api/axiosClient';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Content } = Layout;
const { Title } = Typography;

const SalesItemReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({
        type: '',
        duration: '',
        status: ''
    });

    const columns = [
        {
            title: 'Code',
            dataIndex: 'sp_code',
            key: 'sp_code',
            width: 100,
        },
        {
            title: 'Title',
            dataIndex: 'sp_title',
            key: 'sp_title',
            width: 200,
        },
        {
            title: 'Type',
            dataIndex: 'sp_type',
            key: 'sp_type',
            width: 120,
            render: (type) => (
                <Tag color={type === 'PACKAGE' ? 'purple' : 'blue'}>
                    {type}
                </Tag>
            )
        },
        {
            title: 'Duration',
            dataIndex: 'sp_duration',
            key: 'sp_duration',
            width: 120,
            render: (duration) => {
                const colors = { ONETIME: 'green', DAILY: 'orange', MONTHLY: 'cyan' };
                return <Tag color={colors[duration] || 'default'}>{duration}</Tag>;
            }
        },
        {
            title: 'Price',
            dataIndex: 'sp_uprice',
            key: 'sp_uprice',
            width: 120,
            render: (price) => `Rs. ${Number(price).toLocaleString()}`,
        },
        {
            title: 'Status',
            dataIndex: 'sp_active',
            key: 'sp_active',
            width: 100,
            render: (status) => (
                <Tag color={status == 1 ? 'green' : 'red'}>
                    {status == 1 ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
            )
        },
        {
            title: 'Description',
            dataIndex: 'sp_desc',
            key: 'sp_desc',
            ellipsis: true,
        }
    ];

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.type) params.append('type', filters.type);
            if (filters.duration) params.append('duration', filters.duration);
            if (filters.status) params.append('status', filters.status);

            const response = await axiosClient.get(`/reports/get_sales_item_report.php?${params.toString()}`);
            if (response.data.status === 'success') {
                setData(response.data.data || []);
                if (response.data.data.length === 0) {
                    message.info('No records found for the selected filters.');
                }
            }
        } catch (error) {
            console.error('Fetch report error:', error);
            message.error('Failed to fetch report data.');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFilters({
            type: '',
            duration: '',
            status: ''
        });
        setData([]);
    };

    const exportToExcel = () => {
        if (data.length === 0) return;
        
        const worksheetData = data.map(item => ({
            'Code': item.sp_code,
            'Title': item.sp_title,
            'Type': item.sp_type,
            'Duration': item.sp_duration,
            'Price': item.sp_uprice,
            'Status': item.sp_active == 1 ? 'Active' : 'Inactive',
            'Description': item.sp_desc
        }));

        const ws = XLSX.utils.json_to_sheet(worksheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales Items");
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const finalData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        saveAs(finalData, `Sales_Item_Report_${dayjs().format('YYYYMMDD')}.xlsx`);
    };

    return (
        <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
            <Headerdiv />
            <Content style={{ padding: '24px' }}>
                <div style={{ marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Back to Reports</Button>
                </div>

                {/* Filter Card */}
                <Card 
                    title={
                        <Space>
                            <DollarOutlined />
                            <span>Sales Item Report</span>
                        </Space>
                    }
                    extra={
                        <Space wrap>
                            <Button 
                                type="primary" 
                                icon={<SearchOutlined />} 
                                onClick={fetchReport} 
                                loading={loading}
                            >
                                Generate
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleClear}>Clear</Button>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Item Type</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="Select Type"
                                value={filters.type}
                                onChange={(val) => setFilters({...filters, type: val})}
                            >
                                <Select.Option value="">All Types</Select.Option>
                                <Select.Option value="ITEM">Item</Select.Option>
                                <Select.Option value="PACKAGE">Package</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Duration</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="Select Duration"
                                value={filters.duration}
                                onChange={(val) => setFilters({...filters, duration: val})}
                            >
                                <Select.Option value="">All Durations</Select.Option>
                                <Select.Option value="ONETIME">One Time</Select.Option>
                                <Select.Option value="DAILY">Daily</Select.Option>
                                <Select.Option value="MONTHLY">Monthly</Select.Option>
                                <Select.Option value="ANNUAL">Annual</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Status</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="Select Status"
                                value={filters.status}
                                onChange={(val) => setFilters({...filters, status: val})}
                            >
                                <Select.Option value="">All Status</Select.Option>
                                <Select.Option value="1">Active</Select.Option>
                                <Select.Option value="0">Inactive</Select.Option>
                            </Select>
                        </Col>
                    </Row>
                </Card>

                {/* Results Card */}
                <Card 
                    title={`Report Results ${data.length > 0 ? `(${data.length} records)` : ''}`}
                    extra={
                        data.length > 0 && (
                            <Button 
                                icon={<FileExcelOutlined />} 
                                onClick={exportToExcel}
                                style={{ backgroundColor: '#217346', color: '#fff', borderColor: '#217346' }}
                            >
                                Export to Excel
                            </Button>
                        )
                    }
                >
                    <Table 
                        dataSource={data}
                        columns={columns}
                        rowKey="sp_id"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                        }}
                        bordered
                        size="small"
                        locale={{ emptyText: 'No data. Adjust filters and click Generate.' }}
                    />
                </Card>
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default SalesItemReport;
